import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { DepartmentService } from "services/department.service";
import UserService from "services/user.service";
import { ISaveDepartment, IUpdateDepartment, IUser } from "models/church.types";

export default function DepartmentRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [users, setUsers] = useState<{ value: number; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  const [department, setDepartment] = useState<
    ISaveDepartment | IUpdateDepartment
  >({
    name: "",
    description: "",
    managerId: null,
  });

  // RÉUTILISATION DU STYLE PERSONNALISÉ (Cohérence visuelle)
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused ? "#D4AF37" : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#D4AF37" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 100,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#D4AF37" : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": { backgroundColor: "#D4AF37" },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "0.875rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#475569",
      "&:hover": { color: "#D4AF37" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  useEffect(() => {
    const initData = async () => {
      try {
        const userData = await UserService.getAll(0, 500);
        // Formatage pour react-select
        setUsers(
          userData.data.map((u: IUser) => ({
            value: u.id,
            label: `${u.firstName} ${u.lastName} (${u.login})`,
          })),
        );

        if (isEditMode && id) {
          const data = await DepartmentService.getById(parseInt(id));
          setDepartment({
            id: data.id,
            name: data.name,
            description: data.description || "",
            managerId: data.managerId || null,
          });
        }
      } catch (error) {
        console.error("Erreur d'initialisation:", error);
        toast.error("Erreur lors de la récupération des données");
      }
    };
    initData();
  }, [id, isEditMode]);

  const validate = (): boolean => {
    const newErrors: { name?: string } = {};
    if (!department.name.trim()) {
      newErrors.name = "Le nom du département est obligatoire";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setDepartment((prev) => ({ ...prev, [name]: value }));
    if (errors.hasOwnProperty(name))
      setErrors({ ...errors, [name]: undefined });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEditMode) {
        await DepartmentService.update(department as IUpdateDepartment);
        toast.success("Département mis à jour");
      } else {
        await DepartmentService.create(department as ISaveDepartment);
        toast.success("Département créé avec succès");
      }
      history.push("/admin/departments");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-sitemap mr-2 text-[#D4AF37]"></i>
                {isEditMode ? "Modifier le département" : "Nouveau Département"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Nom du Département */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-tag mr-2"></i>Nom du département
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={department.name}
                    onChange={handleChange}
                    placeholder="Ex: Chorale, Jeunesse, Protocole..."
                    className={`w-full bg-[#0F172A] border ${
                      errors.name ? "border-red-500" : "border-slate-700"
                    } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-[10px] mt-1 italic">
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Responsable (Manager) - AVEC RECHERCHE */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-user-tie mr-2"></i>Responsable
                  </label>
                  <Select
                    options={users}
                    styles={customSelectStyles}
                    isClearable
                    placeholder="Sélectionner ou chercher un responsable..."
                    value={
                      users.find((u) => u.value === department.managerId) ||
                      null
                    }
                    onChange={(selected) => {
                      setDepartment({
                        ...department,
                        managerId: selected ? selected.value : null,
                      });
                    }}
                    noOptionsMessage={() => "Aucun utilisateur trouvé"}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-align-left mr-2"></i>Description
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    value={department.description}
                    onChange={handleChange}
                    placeholder="Décrivez brièvement le rôle de ce département..."
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all resize-none"
                  ></textarea>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-10 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => history.push("/admin/departments")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Créer le département"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
